// Signal — a first-class, mutable "memory record" of a real underlying
// issue, opened by a workflow run when its investigation concludes "this is
// real" (`open_signal`, DEV-16227). The Signal Orchestrator
// (DEV-16226) watches open signals + in-flight runs and cancels/links
// duplicate runs against them. (DEV-16223 / DEV-16224.)
//
// Cross-package contract: these schemas are the source of truth; types are
// inferred, never hand-rolled. (Repo rule #3.)
//
// "Signal" is a provisional name (flagged as a loaded industry term);
// candidates include "incident". Will batch-rename once aligned.
import { z } from 'zod';
import { RunStatusSchema } from './run.js';
export const SignalStatusSchema = z.enum(['OPEN', 'RESOLVED']);
/**
 * Severity of the underlying issue. Ordered low→high; the Inbox surfaces
 * the most severe first (`ORDER BY severity DESC`). Nullable on the row —
 * the opening run may not classify it.
 */
export const SignalSeveritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
/** The full Signal row, as persisted. */
export const SignalSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    /**
     * Stable identity the orchestrator matches new runs against (e.g.
     * `alarm:{deviceId}:{alarmCode}`), computed from the WorkflowVersion's
     * `signalKeyTemplate` at open time. At most one OPEN signal per
     * (orgId, key) at a time.
     */
    key: z.string(),
    status: SignalStatusSchema,
    /** Run whose investigation opened this signal. */
    originRunId: z.string(),
    summary: z.string().nullable(),
    severity: SignalSeveritySchema.nullable(),
    /** Account id of the operator the signal is assigned to (HITL). */
    assignedTo: z.string().nullable(),
    /** Reference to the source signal/alarm that triggered the investigation. */
    sourceSignalId: z.string().nullable(),
    /**
     * Mutable structured fields — the v0 primitive that lets the signal
     * be written by the system and (future) external entities like a
     * ServiceNow writeback. Free-form JSON object.
     */
    fields: z.record(z.string(), z.unknown()),
    createdAt: z.date(),
    updatedAt: z.date(),
    resolvedAt: z.date().nullable(),
    resolvedBy: z.string().nullable(),
    resolvedReason: z.string().nullable(),
});
/**
 * Lightweight projection of a run linked to an signal (origin or
 * deduplicated duplicate). Just enough for the detail view's linked-runs
 * list — not the full Run payload.
 */
export const SignalLinkedRunSchema = z.object({
    id: z.string(),
    workflowId: z.string(),
    status: RunStatusSchema,
    /** Structured cancel reason code for deduplicated runs (e.g. DUPLICATE_OF_*). */
    abortReason: z.string().nullable(),
    /** Human/LLM-readable explanation of why this run was deduplicated. */
    dedupReason: z.string().nullable(),
    /** Which matcher chain layer caught this run. Null for legacy rows. */
    dedupLayer: z.enum(['L1', 'L2', 'L3']).nullable(),
    /** Cosine sim (L2) or LLM confidence (L3) in [0,1]. Null otherwise. */
    dedupConfidence: z.number().min(0).max(1).nullable(),
    createdAt: z.date(),
});
/**
 * List-view projection (Inbox rows, signals list). Carries the
 * headline fields plus a precomputed count of deduplicated runs so the
 * row can show "N duplicates" without a second query.
 */
export const SignalSummarySchema = z.object({
    id: z.string(),
    key: z.string(),
    status: SignalStatusSchema,
    summary: z.string().nullable(),
    severity: SignalSeveritySchema.nullable(),
    assignedTo: z.string().nullable(),
    originRunId: z.string(),
    /** Number of runs the orchestrator deduplicated into this signal. */
    duplicateCount: z.number().int().nonnegative(),
    createdAt: z.date(),
    resolvedAt: z.date().nullable(),
});
/**
 * Full detail payload for the signal detail view (DEV-16229): the
 * signal row, its origin run, and every run deduplicated into it
 * (both `signalId`-linked Mode-A dupes and `cancelledByRunId`-linked
 * Mode-B dupes resolved through the origin run).
 */
export const SignalContextSchema = z.object({
    signal: SignalSchema,
    originRun: SignalLinkedRunSchema.nullable(),
    duplicateRuns: z.array(SignalLinkedRunSchema),
});
// ─── Signal HTTP surface (DEV-16228) ─────────────────────────────────────
/**
 * Query string for `GET /v1/signals`. All filters AND together; omitted
 * fields are not constrained. When `status=RESOLVED` the service sorts by
 * `resolvedAt DESC`; otherwise `createdAt DESC`.
 */
export const SignalListQuerySchema = z
    .object({
    status: SignalStatusSchema.optional(),
    /** Exact match against `Signal.key` — operator-side lookup by dedup identity. */
    key: z.string().min(1).optional(),
    /** Account id of the assignee. Use the empty string to match unassigned. */
    assignedTo: z.string().optional(),
    /** Filter on `createdAt >= from` (ISO datetime). */
    from: z.string().datetime().optional(),
    /** Filter on `createdAt <= to` (ISO datetime). */
    to: z.string().datetime().optional(),
    /**
     * Narrow to one or more severities. The controller accepts a single value
     * or a comma-joined list; the controller pre-splits before validation.
     */
    severity: z.array(SignalSeveritySchema).min(1).max(4).optional(),
    /**
     * Account id of the operator who resolved the signal. Pairs with
     * `status=RESOLVED` for postmortem / audit views ("everything closed by
     * <person>"). Ignored against OPEN rows (resolvedBy is always null there).
     */
    resolvedBy: z.string().min(1).optional(),
    /**
     * Free-text substring search across the operator-facing fields
     * (`key` + `summary`). Case-insensitive ILIKE. Capped at 200 chars
     * to keep the index scan bounded.
     */
    q: z.string().min(1).max(200).optional(),
})
    .strict();
/** Body for `POST /v1/signals/:id/resolve`. Reason is operator-supplied free text. */
export const ResolveSignalInputSchema = z.object({
    reason: z.string().max(2000).optional(),
});
/**
 * Body for `POST /v1/signals/:id/assign`. Pass `null` to clear the
 * assignment; pass an account id to assign to that operator.
 */
export const AssignSignalInputSchema = z.object({
    accountId: z.string().nullable(),
});
/**
 * Payload of an `signal.assigned` event (DEV-16228). Carries the new
 * assignee (or null for "unassigned"); webhook consumers + the live view
 * use it without re-querying.
 */
export const SignalAssignedEventPayloadSchema = z.object({
    signalId: z.string(),
    signalKey: z.string(),
    /** Account id of the new assignee; null when assignment was cleared. */
    assignedTo: z.string().nullable(),
});
// ─── Signal key template ─────────────────────────────────────────────────
//
// The signalKey gives an Signal a stable identity the orchestrator
// matches new runs against. A WorkflowVersion carries a free-form
// `signalKeyTemplate` of `{variable}` placeholders (e.g.
// `alarm:{deviceId}:{alarmCode}`); at run-create time the template is
// resolved against the run's trigger input and stamped on `Run.signalKey`.
// (DEV-16225.) Lives here (next to the signal schemas) so api, runner,
// and node-types can all share one implementation.
// Matches `{name}` where name is a JSON-object key — letters, digits, `_`,
// `$`, `-`, `.`. Kept permissive on purpose; save-time validation is what
// rejects unknown variables, not this regex.
const templateVarPattern = /\{([A-Za-z0-9_$.-]+)\}/g;
/** Return the distinct variable names referenced by a `{var}` template, in order. */
export function extractSignalKeyVars(template) {
    const seen = new Set();
    const out = [];
    for (const m of template.matchAll(templateVarPattern)) {
        const name = m[1];
        if (name && !seen.has(name)) {
            seen.add(name);
            out.push(name);
        }
    }
    return out;
}
/**
 * Resolve a `{variable}` template against a run's trigger input. Variables
 * reference top-level input keys; a missing/nullish value substitutes the
 * empty string (lenient — save-time validation already constrains templates
 * to known fields, and a deterministic empty substitution is preferable to
 * throwing inside the run-create path). Non-string values are coerced with
 * `String()`. Returns null for a null/empty template so callers can store
 * `Run.signalKey = null` cleanly.
 */
export function interpolateSignalKey(template, input) {
    if (!template)
        return null;
    return template.replace(templateVarPattern, (_full, name) => {
        const value = input[name];
        return value === undefined || value === null ? '' : String(value);
    });
}
/**
 * Validate (at workflow-save time) that an signal-key template only
 * references fields declared in the version's `inputSchema`. `knownFields`
 * is the set of top-level `inputSchema.properties` keys. An empty/null
 * template is always valid (the workflow simply isn't deduplicated).
 */
export function validateSignalKeyTemplate(template, knownFields) {
    if (!template)
        return { ok: true, unknownVars: [] };
    const known = new Set(knownFields);
    const unknownVars = extractSignalKeyVars(template).filter((v) => !known.has(v));
    return { ok: unknownVars.length === 0, unknownVars };
}
//# sourceMappingURL=signal.js.map